import { useLocalStorage } from '@vueuse/core'

import { createMemoryHistory, createRouter as createVueRouter } from 'vue-router'
import type { Router } from 'vue-router'

import useOuraAuth from '#web-oura-tools/auth'

import Home from '#ourahub-intra/views/HomeView.vue'

import { useAppStore } from '#ourahub-intra/stores/application'
import { useServicesStore } from '#ourahub-intra/stores/services'

import { getPathFromHash } from '#ourahub-intra/utilities'

import type { Service } from '#ourahub-intra/types/services'

import { logModuleEvent } from '#web-oura-tools/stats/internal'

let initialized = false

let lastLoggedServiceId = ''

let vueRouter: Router | null = null

const ouraAuth = useOuraAuth()

const localStorage = useLocalStorage('ouraHubIntra', {
  app: '',
  env: String(ouraAuth.appEnv.value),
})

const envs = {
  sandbox: ['sandbox'],
  staging: ['sandbox', 'staging'],
  release: ['sandbox', 'staging', 'release'],
}

const appEnv = import.meta.env.VITE_APP_ENV as keyof typeof envs

const logNav = async (serviceId: string): Promise<void> => {
  if (!serviceId || serviceId === 'home') {
    lastLoggedServiceId = ''
    return
  }

  if (serviceId === lastLoggedServiceId) return

  lastLoggedServiceId = serviceId

  useServicesStore().trackServiceOpen(serviceId)

  await logModuleEvent(ouraAuth.appEnv.value, serviceId)
}

const logCurrentLocation = async () => {
  if (window.location.pathname === '/') return

  await logNav(useAppStore().activeService)
}

const updateState = (env?: string, app?: string) => {
  console.debug('Updating route state', env, app, window.location.hash)

  // Path only; drop any ?preset= suffix before parsing env/service.
  const hashPath = getPathFromHash(window.location.hash)

  if (!env) {
    env = hashPath.split('/')[0] || ouraAuth.appEnv.value
  }

  if (!app) {
    app = hashPath.split('/')[1] || 'home'
  }

  localStorage.value = {
    app,
    env,
  }
}

export const updateRoutes = (services: Service[]) => {
  services.forEach((service) => {
    if (envs[appEnv].some((env) => service.entry?.[env]) && !vueRouter?.hasRoute(service.id)) {
      // This causes false warning in console
      vueRouter?.addRoute({
        name: service.id,
        path: `/:env(${envs[appEnv].join('|')})/${service.id}`,
        component: () => import('./views/ModuleLoader.vue'),
      })
    }
  })

  vueRouter?.getRoutes().forEach((route) => {
    if (route.name && route.name !== 'home' && services.length && !services.find((s) => s.id === route.name)) {
      vueRouter?.removeRoute(route.name!)
    }
  })
}

export const createRouter = () => {
  window.addEventListener('focus', () => updateState())

  logModuleEvent(ouraAuth.appEnv.value, 'outo-ourahub-intra')

  if (
    !window.location.hash &&
    localStorage.value.app &&
    localStorage.value.env &&
    window.document.referrer?.startsWith(window.location.origin)
  ) {
    const route = localStorage.value

    window.location.hash = `${route.env}/${route.app}`

    console.debug('Loading stored route', route, window.document.referrer)
  }

  vueRouter = createVueRouter({
    history: createMemoryHistory(),
    routes: [{ name: 'home', path: '/:env?/:service?', component: Home }],
  })

  vueRouter.beforeEach(async (to, from) => {
    const hashPath = getPathFromHash(window.location.hash)

    const curEnv = hashPath.split('/')[0] || ouraAuth.appEnv.value
    const serviceId = hashPath.split('/')[1] || 'home' // Home is the default route

    // We check for allowed env as early as possible to avoid loading an env that is not allowed,
    // when service routes are loaded we check if the requested env and service are valid / exists.

    if (vueRouter!.getRoutes().length <= 1) {
      if (!envs[appEnv].includes(String(curEnv))) {
        console.debug('Invalid environment, loading sys env', appEnv, curEnv, serviceId)

        window.location.hash = `${appEnv}/${serviceId}`

        return { name: serviceId, params: { env: appEnv }, replace: true }
      }
    } else {
      if (!initialized) {
        initialized = true

        if (!vueRouter?.getRoutes().find((r) => r.name === serviceId)) {
          console.debug('Service not found, loading home route', appEnv, curEnv, serviceId)

          return { name: 'home', params: { env: curEnv }, replace: true }
        } else {
          console.debug('Loading initial route', appEnv, curEnv, serviceId, window.location.hash)

          return { name: serviceId, params: { env: curEnv }, replace: true }
        }
      } else {
        if (!vueRouter?.getRoutes().find((r) => r.name === to.name)) {
          console.debug('Service not found, loading home route', appEnv, curEnv, to.name, to.params.env)

          return { name: 'home', params: { env: to.params.env }, replace: true }
        } else {
          window.location.hash = to.path?.slice(1) ?? ''

          console.debug('Current route changed', appEnv, curEnv, from.name, to.name, ouraAuth.appEnv.value)

          updateState(String(to.params.env), String(to.name))

          if (to.name === 'home' && window.location.pathname !== '/') {
            console.debug('Resetting url due to loading home route')

            window.history.pushState({}, '', '/')
          }
        }
      }

      return true
    }

    return false
  })

  vueRouter.afterEach((to) => {
    if (to.name && (vueRouter?.getRoutes().length ?? 0) > 1) {
      logNav(String(to.name))
    }
  })

  const pushState = window.history.pushState

  window.history.pushState = async function (data, title, url) {
    const hash = window.location.hash

    pushState.apply(this, [data, title, url])

    // console.debug('Push state called', url, hash)

    window.location.hash = hash

    await logCurrentLocation()
  }

  const replaceState = window.history.replaceState

  window.history.replaceState = async function (data, title, url) {
    const hash = window.location.hash

    replaceState.apply(this, [data, title, url])

    // console.debug('Replace state called', url, hash)

    window.location.hash = hash

    await logCurrentLocation()
  }

  window.addEventListener('popstate', logCurrentLocation)

  return vueRouter
}
